import { html, css, unsafeCSS, nothing, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { msg, localized } from "@lit/localize";
import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-card";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-carousel";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-pagination";
import type { WhPaginationElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-pagination";
import type { WhCarouselElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-carousel";

import type { HomePageContent, Card } from "../../types/content";
import type { CustomEventDetail, ChangePageEvent } from "../../types/carousel";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-home": WebKioskHomeElement;
  }
}

@localized()
@customElement("web-kiosk-home")
export class WebKioskHomeElement extends WebKioskPageElement {
  private readonly cardsPerPage = 4;

  @state() content: HomePageContent | undefined = undefined;

  @query("wh-pagination") paginationElement: WhPaginationElement | undefined;

  @query("wh-carousel") carousel: WhCarouselElement | undefined;

  @property({ type: Number }) carouselCurrentPageNumber = 1;

  @property({ type: Number }) carouselActiveNumber?: number;

  changePage({ detail }: ChangePageEvent): void {
    this.carouselCurrentPageNumber = detail.newPagePosition;
    this.carouselActiveNumber = detail.newPagePosition - 1;
  }

  updatePagination({ detail: { activeIndex } }: CustomEventDetail): void {
    this.carouselCurrentPageNumber = activeIndex + 1;
  }

  renderCard(card: Card, index: number): TemplateResult {
    const snap = index % this.cardsPerPage === 0;
    return html`<section ?wh-carousel-snap=${snap}>
      <a href=${this.createRelativeLink(card.link)}>
        <wh-card flat size="large">
          <section class="card-inner">
            <img src=${card.icon} alt=${card.title} />
            <h2>${card.title}</h2>
            <p>${card.description}</p>
          </section>
        </wh-card>
      </a>
    </section> `;
  }

  renderPagination(cardsCount: number): TemplateResult | typeof nothing {
    if (cardsCount <= this.cardsPerPage) {
      return nothing;
    }

    const totalPages = Math.ceil(cardsCount / this.cardsPerPage);

    return html`<wh-pagination
      variant="number-of"
      current-page-number=${this.carouselCurrentPageNumber}
      total-pages=${totalPages}
      @paginate=${this.changePage}
    >
      <span slot="prefix">${msg("Page", { id: "home-carousel-prefix" })}</span>
      <span slot="separator"> / </span>
    </wh-pagination>`;
  }

  renderCards(cards: Card[]) {
    return cards.map((card, index) => this.renderCard(card, index));
  }

  renderCarousel(cards: Card[] | undefined): TemplateResult | typeof nothing {
    if (!cards || cards.length === 0) {
      return nothing;
    }

    const cardsToRender = cards;

    const renderedCards = this.renderCards(cardsToRender);

    return html`<wh-carousel
      active=${ifDefined(this.carouselActiveNumber)}
      @scrollcomplete=${this.updatePagination}
    >
      ${renderedCards}
    </wh-carousel> `;
  }

  render(): TemplateResult {
    return html`<section class="page-body">
      <div class="body-inner">
        <section id="header">
          <h1>${this.content?.heading}</h1>
          <img
            src="assets/home/wg-stacked-logo.svg"
            alt=${msg("Walgreens Logo", { id: "walgreens-logo" })}
          />
        </section>
        <section id="cards">
          ${this.renderCarousel(this.content?.cards)}
        </section>
        <section id="info">
          <p>
            <strong>${this.content?.descriptionBold} </strong>${this.content
              ?.description}
          </p>
          ${this.renderPagination(this.content?.cards.length ?? 0)}
        </section>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-text-color: #323264;
    }

    .page-body {
      height: 100%;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/shared/bg/city.svg") right bottom -7% no-repeat,
        url("assets/home/bg/swirl.svg") 50% 50% no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
      background-size: 40%, 100%;
    }

    .body-inner {
      padding: 82px 56px 0;
    }

    section[id="header"] {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    h1 {
      width: 52%;
      margin: 0;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 72px;
      font-weight: 700;
      line-height: 82px;
    }

    section[id="header"] img {
      height: fit-content;
    }

    section[id="cards"] {
      margin-top: 32px;
    }

    wh-carousel section {
      flex: 1 1 0;
    }

    wh-carousel section:not(:last-of-type) {
      padding-right: 32px;
    }

    a,
    wh-card {
      text-align: center;
      text-decoration: none;
    }

    wh-card h2 {
      font-size: 32px;
      font-weight: 700;
      line-height: 38px;
      margin: 0 28px 10px;
      color: var(--web-kiosk-text-color, hotpink);
    }

    wh-card p {
      font-size: 26px;
      font-weight: 400;
      line-height: 40px;
      margin: 0 0 42px;
    }

    .card-inner {
      min-height: 490px;
      min-width: 373px;
    }

    section[id="info"] {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--web-kiosk-text-color, hotpink);
    }

    section[id="info"] p {
      width: 70%;
      font-size: 28px;
      line-height: 48px;
      font-weight: 400;
    }

    wh-pagination {
      font-size: 32px;
    }
  `;
}
